/** Canonical key order for SDUI v1 (slot contract). */

const ROOT_META = ["version"];
const ROOT_BODY = ["tokens", "breakpoints", "elements", "widgets", "chrome", "dataset"];
const TOKEN_KEYS = ["variables", "palette", "semantic"];
const SEMANTIC_KEYS = ["light", "dark"];
const CHROME_PART_KEYS = ["frame", "pages"];

function sortNode(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((entry) => sortNode(entry));
  }
  if (value && typeof value === "object") {
    return sortObjectKeys(value as Record<string, unknown>);
  }
  return value;
}

function sortObjectKeys(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(obj).sort()) {
    out[key] = sortNode(obj[key]);
  }
  return out;
}

function copyKnownThenRest(
  source: Record<string, unknown>,
  knownKeys: string[],
  canonicalizeValue: (key: string, value: unknown) => unknown,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const known = new Set(knownKeys);
  for (const key of knownKeys) {
    if (source[key] != null) {
      out[key] = canonicalizeValue(key, source[key]);
    }
  }
  for (const key of Object.keys(source).sort()) {
    if (known.has(key)) continue;
    out[key] = sortNode(source[key]);
  }
  return out;
}

function canonicalizeTokens(tokens: Record<string, unknown>): Record<string, unknown> {
  const out = copyKnownThenRest(tokens, TOKEN_KEYS, canonicalizeNode);
  if (out.variables && typeof out.variables === "object" && !Array.isArray(out.variables)) {
    out.variables = sortObjectKeys(out.variables as Record<string, unknown>);
  }
  if (out.palette && typeof out.palette === "object" && !Array.isArray(out.palette)) {
    out.palette = sortObjectKeys(out.palette as Record<string, unknown>);
  }
  if (out.semantic && typeof out.semantic === "object") {
    const semantic = copyKnownThenRest(out.semantic as Record<string, unknown>, SEMANTIC_KEYS, (_, v) => v);
    for (const theme of SEMANTIC_KEYS) {
      if (semantic[theme] && typeof semantic[theme] === "object" && !Array.isArray(semantic[theme])) {
        semantic[theme] = sortObjectKeys(semantic[theme] as Record<string, unknown>);
      }
    }
    out.semantic = semantic;
  }
  return out;
}

function canonicalizeChrome(chrome: Record<string, unknown>): Record<string, unknown> {
  const out = copyKnownThenRest(chrome, CHROME_PART_KEYS, canonicalizeNode);
  for (const part of CHROME_PART_KEYS) {
    if (part === "pages") continue;
    const value = out[part];
    if (value && typeof value === "object" && !Array.isArray(value) && Object.keys(value as object).length === 0) {
      delete out[part];
    }
  }
  if (out.pages && typeof out.pages === "object") {
    out.pages = sortObjectKeys(out.pages as Record<string, unknown>);
  }
  return out;
}

function canonicalizeNode(key: string, value: unknown): unknown {
  if (value == null) return value;
  if (key === "tokens" && typeof value === "object" && !Array.isArray(value)) {
    return canonicalizeTokens(value as Record<string, unknown>);
  }
  if (key === "chrome" && typeof value === "object" && !Array.isArray(value)) {
    return canonicalizeChrome(value as Record<string, unknown>);
  }
  if (key === "elements" && Array.isArray(value)) {
    return value.map((entry) => sortNode(entry));
  }
  if ((key === "widgets" || key === "dataset") && typeof value === "object" && !Array.isArray(value)) {
    return sortObjectKeys(value as Record<string, unknown>);
  }
  return sortNode(value);
}

export function canonicalizeSduiDocument(source: Record<string, unknown>): Record<string, unknown> {
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return source;
  }
  const knownRoot = new Set([...ROOT_META, ...ROOT_BODY]);
  const out: Record<string, unknown> = {};
  for (const key of ROOT_META) {
    if (source[key] != null) {
      out[key] = canonicalizeNode(key, source[key]);
    }
  }
  for (const key of ROOT_BODY) {
    if (source[key] != null) {
      out[key] = canonicalizeNode(key, source[key]);
    }
  }
  for (const key of Object.keys(source).sort()) {
    if (knownRoot.has(key)) continue;
    out[key] = sortNode(source[key]);
  }
  return out;
}
